// Host-side App SDK operations use the person's session-bound fetch and captured saved selection.
// Success unwraps the server's owner value. Changed-session refusals retire the frame.
// Private image failures reject through the opaque SDK channel; retained image bytes may retry one
// fetch rejection while the same document remains mounted. Data writes and provider reads do not retry.
import { runtimeErrorResponse } from '../../../../src/apps/runtime/runtime-error-response.mjs';
import { presentSdkFailure } from '../../../../src/apps/runtime/tier0/index.mjs';
import {
  createCachedReadSession,
  type CachedRead,
  type CachedReadSession,
  type CachedSeed,
} from '../../../../src/apps/runtime/cached-read-session.mjs';
export const RUNTIME_READ_OPERATIONS = [
  'collections.query',
  'sources.read',
  'tools.call',
  'collections.mutateBatch',
  'collections.lookupReceipt',
  'collections.history',
  'collections.checkWrite',
  'collections.undo',
  'connections.list',
  'connections.read',
  'images.read',
  'functions.call',
  'functions.sync',
  'functions.syncStatus',
  'ai.run',
  'files.read',
  'files.write',
  'identity.user',
  'identity.can',
  'team.list',
] as const;
export type RuntimeReadOperation = (typeof RUNTIME_READ_OPERATIONS)[number];
// Reads that end with their caller: the App's own abort, or its document retiring (the person navigates away, a
// reopen replaces it), so a retired document leaves no request behind to be refused. A write, a paid or provider
// read, an effect's receipt lookup and an image keep their reply.
const CANCELLED_WITH_CALLER: ReadonlySet<RuntimeReadOperation> = new Set([
  'collections.query',
  'collections.history',
  'connections.list',
  'connections.read',
  'files.read',
  'identity.user',
  'identity.can',
  'team.list',
]);

export type RuntimeSelection = {
  artifactId: string;
  revisionId: string;
  versionId: string;
  purpose: string;
};
type Failure = { ok: false; error: { code: string; retryable: boolean; userMessage?: string } };
type CacheDenial = { cacheDenied?: true; statusCode: number };
const failure = (
  code: string,
  retryable = false,
  denial?: CacheDenial,
  userMessage?: string,
): Failure => {
  const error = { code, retryable };
  // This classification belongs to the host cache. It is not SDK copy or a
  // provider field and never becomes part of the serialized guest reply.
  if (denial?.cacheDenied)
    Object.defineProperties(error, {
      cacheDenied: { value: true },
      statusCode: { value: denial.statusCode },
    });
  // The server's own sentence for this refusal (a payment refusal), presented instead of the host's copy for its
  // code. Not enumerable: the failure's shape stays the canonical one.
  if (userMessage) Object.defineProperty(error, 'userMessage', { value: userMessage });
  return { ok: false, error };
};

const operationPath = (artifactId: string) =>
  `/api/artifacts/${encodeURIComponent(artifactId)}/runtime/operation`;

/**
 * The wire is `{ok:true,result:ownerValue}` or `{ok:false,error:{code,retryable}}`. The host transport wraps
 * what a handler returns as `{ok:true,result:value}` itself, and the SDK parses `value` (a Dataset for
 * collections.query) or an `{ok:false,error}`, so success is UNWRAPPED here; anything else is "unavailable".
 */
function sdkResult(value: unknown): unknown {
  const raw = value as { ok?: unknown; result?: unknown; error?: { code?: unknown } } | null;
  if (raw && typeof raw === 'object' && raw.ok === true && 'result' in raw) return raw.result;
  if (raw && typeof raw === 'object' && raw.ok === false && typeof raw.error?.code === 'string')
    return value;
  return failure('UNAVAILABLE');
}

type CacheMode = 'open' | 'refresh';
type CachedEnvelope = Omit<CachedRead, 'operation' | 'input' | 'value'>;
type Sent = { value: unknown; view?: CachedEnvelope };
/** Only a complete admitted envelope is kept; anything else is an ordinary read result. */
const cachedEnvelope = (view: unknown): CachedEnvelope | undefined => {
  const v = view as Partial<CachedEnvelope> | null;
  return v &&
    typeof v === 'object' &&
    typeof v.key === 'string' &&
    Number.isSafeInteger(v.generation) &&
    typeof v.fetchedAt === 'string' &&
    ['idle', 'refreshing', 'failed'].includes(v.status as string) &&
    typeof v.stale === 'boolean'
    ? (v as CachedEnvelope)
    : undefined;
};
type RuntimeOperationOptions = Parameters<typeof createRuntimeOperations>[0];

/** The handler map alone (no cached reads), for callers that only need the operations. */
export function createRuntimeOperationHandlers(options: RuntimeOperationOptions) {
  return createRuntimeOperations(options).handlers;
}

/**
 * The operations plus, when the issued bootstrap carried cached reads, the document-scoped cached read session:
 * connections.read answers from the admitted seed at once and refreshes only through the session; the App's own
 * collections.query and every other operation keep their existing live path.
 */
export function createRuntimeOperations({
  session,
  apiUrl,
  selection,
  documentNonce,
  privateChatApp = () => false,
  approveWrite,
  errorMessage,
  cache,
}: {
  session: typeof fetch;
  apiUrl: (path: string) => string;
  selection: RuntimeSelection;
  /** The nonce of the document that is mounted NOW (issued.scope.documentNonce). */
  documentNonce: () => string;
  privateChatApp?: () => boolean;
  /** Private host dialog/route. Never passed into the generated App. */
  approveWrite?: (request: {
    operation: 'files.write' | 'collections.undo';
    input: unknown;
    documentNonce: string;
  }) => Promise<boolean>;
  /** The reader's sentence for a typed failure code (host locale). Presentation only: the canonical failure,
   * its code and its classification are unchanged. */
  errorMessage?: (code: string) => string;
  /** The bootstrap's admitted cached reads for THIS document, and what to do when access is gone. */
  cache?: { seed?: CachedSeed; onDenied?: (error: unknown) => void };
}) {
  // A resolved failure carries the sentence as non-enumerable presentation; a thrown typed host error carries it
  // as userMessage. The server's own sentence (a payment refusal) is kept; otherwise the host's copy for the code.
  // Successes, session refusals and anything without a typed code pass through untouched.
  const present = (value: unknown) => {
    const error = (value as Failure | null)?.error;
    if ((value as Failure | null)?.ok !== false || typeof error?.code !== 'string') return value;
    const message = error.userMessage ?? errorMessage?.(error.code);
    return message ? presentSdkFailure(value, message) : value;
  };
  const presentThrown = (error: unknown) => {
    const typed = error as { code?: unknown; userMessage?: unknown } | null;
    if (errorMessage && typed && typeof typed.code === 'string' && typed.userMessage === undefined)
      typed.userMessage = errorMessage(typed.code);
    return error;
  };
  const send = async (
    operation: RuntimeReadOperation,
    input: unknown,
    signal?: AbortSignal,
    cacheMode?: CacheMode,
  ): Promise<Sent> => {
    const sent = (value: unknown, view?: unknown): Sent => {
      const admitted = cachedEnvelope(view);
      return admitted ? { value, view: admitted } : { value };
    };
    const imageRead = privateChatApp() && operation === 'images.read';
    // The SDK binds these effects to their original operation key. A lost
    // reply must reach effectRequest's uncertainty channel, never read retry.
    const nativeWrite = operation === 'files.write' || operation === 'collections.undo';
    const backendEffect =
      operation === 'functions.call' ||
      operation === 'functions.sync' ||
      operation === 'ai.run' ||
      nativeWrite;
    const managedImage =
      imageRead && typeof (input as { imageId?: unknown } | null)?.imageId === 'string';
    const refuse = (
      code: Parameters<typeof failure>[0],
      retryable = false,
      details?: Record<string, unknown>,
      denial?: CacheDenial,
      userMessage?: string,
    ): Failure => {
      // Private images need the transport's rejection channel; a resolved failure has no image src. An effect's
      // refusal stays a thrown one: the SDK keeps its original key and reports it unknown, sentence or not.
      if (imageRead || backendEffect || details)
        throw Object.assign(new Error(code), {
          code,
          ...(denial?.cacheDenied ? denial : {}),
          ...(details ? { details, readDetail: details } : {}),
          ...(userMessage ? { userMessage } : {}),
        });
      return failure(code, retryable, denial, userMessage);
    };
    const refused = (...args: Parameters<typeof refuse>) => sent(refuse(...args));
    const cancellable = CANCELLED_WITH_CALLER.has(operation);
    const nonce = documentNonce();
    if (!nonce) return refused('UNAVAILABLE');
    const { artifactId, revisionId, versionId, purpose } = selection;
    const url = apiUrl(operationPath(artifactId));
    const request: RequestInit = {
      method: 'POST',
      ...(cancellable ? { signal } : {}),
      credentials: 'same-origin',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({
        revisionId,
        versionId,
        purpose,
        documentNonce: nonce,
        operation,
        input: input ?? null,
        ...(cacheMode ? { cacheMode } : {}),
      }),
    };
    let response: Response;
    for (let attempt = 0; ; attempt++) {
      try {
        response = await session(url, request);
        break;
      } catch (error) {
        if (cancellable) signal?.throwIfAborted();
        const name = (error as { name?: string } | null)?.name;
        if (name === 'SessionChangedError' || name === 'SessionUnverifiedError') throw error;
        // Only retained image bytes are an ordinary read. Provider selections and writes never retry.
        if (
          attempt === 0 &&
          managedImage &&
          error instanceof TypeError &&
          privateChatApp() &&
          documentNonce() === nonce
        )
          continue;
        return refused('UNAVAILABLE', true);
      }
    }
    // A database read's classified refusal (time limit, replica, unreachable) reaches
    // the App with its numbers instead of a generic UNAVAILABLE.
    if (!response.ok) {
      const typed = runtimeErrorResponse(await response.json().catch(() => null), response.status);
      // The decoder names the server's curated sentence only for a payment refusal (never a body's own message).
      const curated = (typed as { userMessage?: unknown } | null)?.userMessage;
      if (typed)
        return refused(
          typed.code,
          typed.retryable,
          operation === 'connections.read' ? typed.details : undefined,
          { cacheDenied: typed.cacheDenied, statusCode: response.status },
          typeof curated === 'string' ? curated : undefined,
        );
    }
    if (response.status === 403) return refused('NOT_AUTHORIZED');
    if (response.status === 400) return refused('INVALID_INPUT');
    if (response.status === 413) return refused('LIMIT_EXCEEDED');
    if (!response.ok) return refused('UNAVAILABLE', response.status >= 500);
    try {
      let body = (await response.json()) as { view?: unknown } | null;
      let result = sdkResult(body) as {
        ok?: boolean;
        error?: { code?: string };
      };
      if (nativeWrite && result?.ok === false && result.error?.code === 'APPROVAL_REQUIRED') {
        if (
          !approveWrite ||
          !(await approveWrite({
            operation,
            input: JSON.parse(String(request.body)).input,
            documentNonce: nonce,
          }))
        )
          return sent(failure('NOT_AUTHORIZED'));
        if (documentNonce() !== nonce) return sent(failure('NOT_AUTHORIZED'));
        // The first reply proved no new effect was admitted. Explicit host
        // confirmation now permits one SAME-body submission, never a retry
        // after network/commit uncertainty and never a new operation key.
        const confirmed = await session(url, request);
        if (!confirmed.ok)
          return refused(confirmed.status === 403 ? 'NOT_AUTHORIZED' : 'UNAVAILABLE');
        body = (await confirmed.json()) as typeof body;
        result = sdkResult(body) as typeof result;
      }
      if (privateChatApp() && result?.ok === false)
        throw Object.assign(new Error(result.error?.code ?? 'UNAVAILABLE'), {
          code: result.error?.code ?? 'UNAVAILABLE',
        });
      // A cached read's admitted envelope rides beside the owner value; writes never carry one.
      return sent(result, nativeWrite ? undefined : body?.view);
    } catch (error) {
      if (privateChatApp() && typeof (error as { code?: unknown })?.code === 'string') throw error;
      return refused('UNAVAILABLE');
    }
  };
  const reads = cache
    ? createCachedReadSession({
        seed: cache.seed,
        load: (operation, input, options) =>
          send(operation as RuntimeReadOperation, input, options?.signal, options?.cacheMode),
        onDenied: cache.onDenied,
      })
    : null;
  // The host's own Sync now never touches the App's cached view: its refusal is said under the bar, not as lost access.
  const call = (operation: RuntimeReadOperation, input: unknown, signal?: AbortSignal) =>
    reads && operation !== 'functions.sync'
      ? reads.request(operation, input, signal ? { signal } : undefined)
      : send(operation, input, signal).then((result) => result.value);
  const handlers = Object.fromEntries(
    RUNTIME_READ_OPERATIONS.map((operation) => [
      operation,
      {
        request: (input: unknown, { signal }: { signal?: AbortSignal } = {}) =>
          call(operation, input, signal).then(present, (error) => {
            throw presentThrown(error);
          }),
      },
    ]),
  ) as Record<
    RuntimeReadOperation,
    { request: (input: unknown, options?: { signal?: AbortSignal }) => Promise<unknown> }
  >;
  return { handlers, cache: reads as CachedReadSession | null };
}
